import { Component, OnInit } from '@angular/core';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';
import { ToastService } from '../core/toast.service';
import { TITLES, graduationYears } from '../core/app-date.pipe';

@Component({
  selector: 'app-user-management',
  template: `
  <h1 class="page-title">Accounts Review and Activation</h1>
  <p class="muted">Approve new members, assign roles and manage passwords.</p>

  <div class="tabs">
    <a [class.active]="status === 'PENDING'" (click)="setStatus('PENDING')">Pending</a>
    <a [class.active]="status === 'ACTIVE'" (click)="setStatus('ACTIVE')">Active</a>
    <a [class.active]="status === 'INACTIVE'" (click)="setStatus('INACTIVE')">Inactive</a>
    <a [class.active]="status === 'REJECTED'" (click)="setStatus('REJECTED')">Rejected</a>
    <a [class.active]="status === ''" (click)="setStatus('')">All</a>
  </div>

  <div class="filters">
    <select *ngIf="auth.isSuperAdmin" [(ngModel)]="orgId" (ngModelChange)="load()">
      <option [ngValue]="null">All organizations</option>
      <option *ngFor="let o of orgs" [ngValue]="o.organizationId">{{ o.orgName }}</option>
    </select>
    <input [(ngModel)]="search" (keyup.enter)="load()" placeholder="Search name or email…">
    <button class="btn sm" (click)="load()">Search</button>
  </div>

  <div class="alert ok" *ngIf="shownPassword">
    Password for <b>{{ shownPassword.name }}</b>: <code>{{ shownPassword.pwd }}</code> — share it with them securely.
    <a (click)="shownPassword = null">Dismiss</a>
  </div>

  <div class="card flush">
    <div class="empty" *ngIf="!users.length">No users here.</div>
    <div class="table-scroll" *ngIf="users.length">
      <table class="grid">
        <thead>
          <tr><th>Name</th><th>Contact</th><th *ngIf="auth.isSuperAdmin">Organization</th><th>Branch</th><th>Role</th>
              <th>Pwd expiry (hrs)</th><th>Résumés</th><th>Signed up</th><th></th></tr>
        </thead>
        <tbody>
          <tr *ngFor="let u of users">
            <td>
              <button type="button" class="linkish" (click)="openUser(u)" title="Open profile">
                <b>{{ u.title }} {{ u.firstName }} {{ u.lastName }}</b>
              </button>
              <div class="small muted">{{ u.username }}<span *ngIf="u.graduationClass"> · Class of {{ u.graduationClass }}</span></div>
              <span class="chip" [ngClass]="u.status.toLowerCase()">{{ u.status }}</span>
              <div class="small" *ngIf="u.tempPasswordVisible">Generated pwd: <code>{{ u.tempPasswordVisible }}</code></div>
            </td>
            <td class="small">{{ u.email }}<br>{{ u.cellPhone }}<br>{{ u.city }}<span *ngIf="u.state">, {{ u.state }}</span></td>
            <td *ngIf="auth.isSuperAdmin">{{ u.orgName }}</td>
            <td>{{ u.branchName }}</td>
            <td>
              <select [(ngModel)]="u._role" [disabled]="u.roleCode === 'SUPER_ADMIN' && !auth.isSuperAdmin">
                <option [ngValue]="null">— choose —</option>
                <option *ngFor="let r of rolesFor(u)" [ngValue]="r.roleId">{{ r.roleName }}</option>
              </select>
              <button class="btn xs" *ngIf="u.status !== 'PENDING' && u._role && u._role !== u.roleId" (click)="setRole(u)">Save</button>
            </td>
            <td>
              <ng-container *ngIf="u.roleCode !== 'SUPER_ADMIN' && u.roleCode !== 'ADMIN' && u.status === 'ACTIVE'; else unlimited">
                <input class="num" type="number" min="1" [(ngModel)]="u._exp" placeholder="∞">
                <button class="btn xs" (click)="setExpiry(u)">Set</button>
              </ng-container>
              <ng-template #unlimited><span class="muted small">{{ u.status === 'ACTIVE' ? 'Unlimited' : '—' }}</span></ng-template>
            </td>
            <td>{{ u.resumeCount }}</td>
            <td class="small">{{ u.createdAt | appDate }}</td>
            <td class="actions">
              <ng-container *ngIf="u.status === 'PENDING'">
                <button class="btn xs primary" (click)="approve(u)" [disabled]="busy[u.userId]">{{ busy[u.userId] ? 'Approving…' : 'Approve' }}</button>
                <button class="btn xs danger" (click)="reject(u)" [disabled]="busy[u.userId]">Reject</button>
              </ng-container>
              <button class="btn xs primary" *ngIf="u.status === 'REJECTED'" (click)="approve(u)" [disabled]="busy[u.userId]">{{ busy[u.userId] ? 'Approving…' : 'Approve' }}</button>
              <ng-container *ngIf="u.status === 'ACTIVE' || u.status === 'INACTIVE'">
                <button class="btn xs" (click)="openUser(u)">Edit</button>
                <button class="btn xs" (click)="resetPassword(u)">Reset pwd</button>
                <button class="btn xs" *ngIf="!u.isSystem && u.userId !== auth.user.userId" (click)="toggle(u)">
                  {{ u.status === 'ACTIVE' ? 'Deactivate' : 'Activate' }}
                </button>
              </ng-container>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- ============ User profile drawer ============ -->
  <div class="modal-back" *ngIf="edit" (click)="closeUser()">
    <div class="drawer" (click)="$event.stopPropagation()">
      <div class="drawer-head">
        <h3>{{ edit.firstName }} {{ edit.lastName }}</h3>
        <span class="chip" [ngClass]="edit.status?.toLowerCase()">{{ edit.status }}</span>
        <button class="x" type="button" (click)="closeUser()" aria-label="Close">×</button>
      </div>

      <form class="drawer-body" #ef="ngForm" (ngSubmit)="saveUser()">
        <p class="meta-line">
          {{ edit.orgName }} · {{ edit.roleName || 'No role yet' }}
          · Signed up {{ edit.createdAt | appDate }}<span *ngIf="edit.lastLoginAt"> · Last sign-in {{ edit.lastLoginAt | appDate:true }}</span>
        </p>

        <div class="fgrid">
          <div><label class="req">Username</label><input name="username" [(ngModel)]="edit.username" required maxlength="150"></div>
          <div><label class="req">Email</label><input name="email" type="email" [(ngModel)]="edit.email" required email></div>
          <div><label>Title</label>
            <select name="title" [(ngModel)]="edit.title"><option [ngValue]="null">—</option><option *ngFor="let t of titles" [value]="t">{{ t }}</option></select>
          </div>
          <div><label>Cell Phone</label><input name="phone" [(ngModel)]="edit.cellPhone"></div>
          <div><label class="req">First Name</label><input name="fn" [(ngModel)]="edit.firstName" required></div>
          <div><label>Middle Name</label><input name="mn" [(ngModel)]="edit.middleName"></div>
          <div><label class="req">Last Name</label><input name="ln" [(ngModel)]="edit.lastName" required></div>
          <div><label>Role</label>
            <select name="role" [(ngModel)]="edit._role" [disabled]="edit.roleCode === 'SUPER_ADMIN' && !auth.isSuperAdmin">
              <option *ngFor="let r of rolesFor(edit)" [ngValue]="r.roleId">{{ r.roleName }}</option>
            </select>
          </div>
          <div><label>Branch</label>
            <select name="branch" [(ngModel)]="edit.branchId"><option *ngFor="let b of edit.branches" [ngValue]="b.branchId">{{ b.branchName }}</option></select>
          </div>
          <div class="wide"><label>Address</label><input name="addr" [(ngModel)]="edit.address"></div>
          <div><label>City</label><input name="city" [(ngModel)]="edit.city"></div>
          <div><label>State</label><input name="state" [(ngModel)]="edit.state"></div>
          <div><label>Zip Code</label><input name="zip" [(ngModel)]="edit.zipCode"></div>
          <div><label>Graduation Class</label>
            <select name="grad" [(ngModel)]="edit.graduationClass"><option [ngValue]="null">Not applicable</option><option *ngFor="let y of years" [value]="y">{{ y }}</option></select>
          </div>
        </div>

        <!-- Password help -->
        <div class="pwd-box">
          <h4>Password</h4>
          <div class="muted small">Last changed {{ edit.passwordChangedAt ? (edit.passwordChangedAt | appDate:true) : '—' }}.</div>

          <ng-container *ngIf="edit.canViewPassword">
            <div class="pwd-reveal" *ngIf="!shownPwd">
              <button type="button" class="btn sm" (click)="viewPassword()" [disabled]="!edit.hasStoredPassword || pwdBusy">Show last password</button>
              <span class="muted small" *ngIf="!edit.hasStoredPassword">Not stored for this account (set before this feature). Use Reset password instead.</span>
            </div>
            <div class="pwd-reveal" *ngIf="shownPwd">
              <code>{{ shownPwd }}</code>
              <button type="button" class="btn xs" (click)="copy(shownPwd)">Copy</button>
              <button type="button" class="btn xs ghost" (click)="shownPwd = null">Hide</button>
            </div>
            <div class="muted small" *ngIf="edit.passwordLastViewedAt" style="margin-top:6px">
              Last looked up {{ edit.passwordLastViewedAt | appDate:true }}. Every lookup is logged.
            </div>
          </ng-container>

          <div class="pwd-reveal">
            <button type="button" class="btn sm" (click)="resetPassword(edit)">Reset password…</button>
          </div>
        </div>
      </form>

      <div class="drawer-foot">
        <button type="button" class="btn ghost" (click)="closeUser()">Cancel</button>
        <button type="button" class="btn primary" (click)="saveUser()" [disabled]="saving || !ef?.valid">{{ saving ? 'Saving…' : 'Save Profile' }}</button>
      </div>
    </div>
  </div>`
})
export class UserManagementComponent implements OnInit {
  users: any[] = [];
  roles: any[] = [];
  orgs: any[] = [];
  status = 'PENDING';
  orgId: number = null;
  search = '';
  shownPassword: { name: string; pwd: string } = null;
  busy: { [userId: number]: boolean } = {};

  // profile drawer
  edit: any = null;
  saving = false;
  shownPwd: string = null;
  pwdBusy = false;
  titles = TITLES;
  years = graduationYears();

  constructor(private api: ApiService, public auth: AuthService, private toast: ToastService) {}

  ngOnInit() {
    if (this.auth.isSuperAdmin) { this.api.get<any[]>('/organizations').subscribe(o => this.orgs = o); }
    this.load();
  }

  setStatus(s: string) { this.status = s; this.load(); }

  load() {
    this.api.get<any[]>('/users', { status: this.status, organizationId: this.orgId, search: this.search }).subscribe(list => {
      list.forEach(u => { u._role = u.roleId; u._exp = u.passwordExpiryHours; });
      this.users = list;
      // roles for every org shown (system roles + each org's custom roles)
      const orgIds = Array.from(new Set(list.map(u => u.organizationId)));
      this.roles = [];
      orgIds.forEach(id => this.api.get<any[]>('/users/roles', { organizationId: id }).subscribe(rs => {
        rs.forEach(r => { if (!this.roles.some(x => x.roleId === r.roleId)) { this.roles.push(r); } });
      }));
    }, e => this.toast.err(ApiService.message(e)));
  }

  /** Makes sure the role list for this user's organization is loaded (the table only loads roles for orgs it shows). */
  private loadRolesFor(orgId: number) {
    this.api.get<any[]>('/users/roles', { organizationId: orgId }).subscribe(rs => {
      rs.forEach(r => { if (!this.roles.some(x => x.roleId === r.roleId)) { this.roles.push(r); } });
    });
  }

  rolesFor(u: any) { return this.roles.filter(r => r.isSystem || r.organizationId === u.organizationId); }

  approve(u: any) {
    if (this.busy[u.userId]) { return; }          // ignore double-clicks while the first request is running
    this.busy[u.userId] = true;
    this.api.post('/users/' + u.userId + '/approve', { roleId: u._role }).subscribe(r => {
      this.busy[u.userId] = false;
      this.toast.ok(r.message);
      if (r.generatedPassword) { this.shownPassword = { name: u.firstName + ' ' + u.lastName, pwd: r.generatedPassword }; }
      this.load();
    }, e => { this.busy[u.userId] = false; this.toast.err(ApiService.message(e)); this.load(); });
  }

  reject(u: any) {
    if (this.busy[u.userId]) { return; }
    if (!confirm('Reject ' + u.firstName + ' ' + u.lastName + '?')) { return; }
    this.busy[u.userId] = true;
    this.api.post('/users/' + u.userId + '/reject').subscribe(r => {
      this.busy[u.userId] = false; this.toast.ok(r.message); this.load();
    }, e => { this.busy[u.userId] = false; this.toast.err(ApiService.message(e)); this.load(); });
  }

  setRole(u: any) {
    this.api.post('/users/' + u.userId + '/role', { roleId: u._role }).subscribe(r => { this.toast.ok(r.message); this.load(); }, e => this.toast.err(ApiService.message(e)));
  }

  setExpiry(u: any) {
    const hours = u._exp === '' || u._exp === null || u._exp === undefined ? null : +u._exp;
    this.api.post('/users/' + u.userId + '/password-expiry', { hours }).subscribe(r => this.toast.ok(r.message), e => this.toast.err(ApiService.message(e)));
  }

  resetPassword(u: any) {
    const pwd = prompt('New password for ' + u.firstName + ' (leave blank to generate one):', '');
    if (pwd === null) { return; }
    this.api.post('/users/' + u.userId + '/reset-password', { newPassword: pwd }).subscribe(r => {
      this.toast.ok(r.message);
      this.shownPassword = { name: u.firstName + ' ' + u.lastName, pwd: r.newPassword };
      if (this.edit && this.edit.userId === u.userId) { this.closeUser(); }
    }, e => this.toast.err(ApiService.message(e)));
  }

  openUser(u: any) {
    this.shownPwd = null;
    this.api.get('/users/' + u.userId).subscribe((d: any) => {
      d._role = d.roleId;
      this.edit = d;
      this.loadRolesFor(d.organizationId);
    }, e => this.toast.err(ApiService.message(e)));
  }

  closeUser() { this.edit = null; this.shownPwd = null; }

  saveUser() {
    if (!this.edit) { return; }
    this.saving = true;
    const e = this.edit;
    const body = {
      username: e.username, email: e.email, title: e.title, firstName: e.firstName, middleName: e.middleName, lastName: e.lastName,
      address: e.address, city: e.city, state: e.state, zipCode: e.zipCode, cellPhone: e.cellPhone,
      graduationClass: e.graduationClass, branchId: e.branchId
    };
    this.api.put('/users/' + e.userId, body).subscribe(r => {
      const done = (msg: string) => { this.saving = false; this.toast.ok(msg); this.closeUser(); this.load(); };
      // Role changed in the panel? Save it too (pending users keep it until they're approved).
      if (e._role && e._role !== e.roleId) {
        this.api.post('/users/' + e.userId + '/role', { roleId: e._role }).subscribe(
          () => done('Profile and role updated.'),
          err => { this.saving = false; this.toast.err(ApiService.message(err)); this.load(); });
      } else { done(r.message); }
    }, err => { this.saving = false; this.toast.err(ApiService.message(err)); });
  }

  viewPassword() {
    this.pwdBusy = true;
    this.api.get('/users/' + this.edit.userId + '/password').subscribe(r => {
      this.pwdBusy = false;
      this.shownPwd = r.password;
      this.edit.passwordLastViewedAt = new Date().toISOString();
    }, err => { this.pwdBusy = false; this.toast.err(ApiService.message(err)); });
  }

  copy(text: string) {
    const done = () => this.toast.ok('Copied.');
    const nav: any = navigator;
    if (nav.clipboard && nav.clipboard.writeText) { nav.clipboard.writeText(text).then(done, () => this.fallbackCopy(text, done)); }
    else { this.fallbackCopy(text, done); }
  }

  private fallbackCopy(text: string, done: () => void) {
    const t = document.createElement('textarea');
    t.value = text; document.body.appendChild(t); t.select();
    try { document.execCommand('copy'); done(); } catch (e) { this.toast.err('Copy failed — select the text and press Ctrl+C.'); }
    document.body.removeChild(t);
  }

  toggle(u: any) {
    const next = u.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    this.api.post('/users/' + u.userId + '/status/' + next).subscribe(r => { this.toast.ok(r.message); this.load(); }, e => this.toast.err(ApiService.message(e)));
  }
}
